/**
 * Strings for the security scan screens.
 *
 * Every user-facing string lives here with its English text, so the screens
 * render correctly before the locale files are updated and the merger has one
 * object to copy from. Keys are the i18n keys; `{{x}}` is i18next
 * interpolation; `_one` / `_other` pairs are plurals driven by `count`.
 *
 * NOT covered here, deliberately: the per-finding `title`, `detail` and
 * `remediation` text, which the scanner API returns as English prose. They are
 * shown verbatim. A finding's title can be translated by adding a key
 * `scans.finding.<FINDING_KEY>` to the locale files; the screen looks for it.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api';
import { useApiError } from '../../lib/useApiError';

/** The groups of explanation a finding can map to. */
export type TopicId =
  | 'dnsResolve' | 'dnsIpv6' | 'dnsNs'
  | 'spf' | 'dmarc' | 'mx'
  | 'tlsReach' | 'tlsProtocol' | 'tlsCert' | 'tlsTrust'
  | 'httpReach' | 'hsts' | 'csp' | 'xcto' | 'xfo' | 'referrer' | 'serverBanner'
  | 'unknown';

export const SCAN_STRINGS = {
  // --- Page ---------------------------------------------------------------
  'scans.title': 'Security scans',
  'scans.subtitle': 'Passive checks of how a domain is set up: DNS, email protection, HTTPS certificate and web security headers.',
  'scans.credits_one': '{{count}} credit',
  'scans.credits_other': '{{count}} credits',
  'scans.priceLine': 'Each scan costs {{credits}}.',
  'scans.newScan': 'Scan a domain…',
  'scans.noAccess': 'You do not have permission to view security scans.',
  'scans.loadFailed': 'Could not load your scans. This is an error, not an empty result: your scans may exist and simply could not be retrieved.',
  'scans.refreshFailed': 'Could not refresh the scan list. The scans shown may be out of date.',
  'scans.truncated': 'Only the {{count}} most recent scans are loaded. Older scans and some schedule details are not shown.',

  // --- Scope notice ---------------------------------------------------------
  'scans.scope.title': 'What these scans do, and do not do',
  'scans.scope.does': 'They passively read what a domain publishes openly: its DNS set-up, its email sender protection (SPF, DMARC and mail servers), its HTTPS certificate and its web security headers. Each check is one ordinary request, like a browser or a mail server would make.',
  'scans.scope.doesNot': 'They do not test for software vulnerabilities, open ports, weak passwords, malware or anything inside the systems behind the domain. A scan with no issues means these particular settings looked correct. It is not proof that the domain is secure.',
  'scans.scope.show': 'Read more about what is checked',
  'scans.scope.hide': 'Hide',
  'scans.scope.checkedList': 'Checked: DNS address and nameservers · SPF · DMARC · mail servers · HTTPS certificate and protocol version · five web security headers · server version banner.',
  'scans.scope.bareDomain': 'The bare domain is scanned (without “www.”).',

  // --- Tiles ---------------------------------------------------------------
  'scans.tile.domains': 'Domains scanned',
  'scans.tile.domainsHint': 'with at least one completed scan',
  'scans.tile.critical': 'With critical findings',
  'scans.tile.criticalHint': 'latest completed scan of each domain',
  'scans.tile.upcoming': 'Paid runs still to come',
  'scans.tile.upcomingHint': 'already paid for',
  'scans.tile.failed': 'Failed runs',
  'scans.tile.failedHint': 'no result; can be retried at no charge',

  // --- List ----------------------------------------------------------------
  'scans.list.title': 'Scans',
  'scans.list.search': 'Search by domain or supplier…',
  'scans.list.filterStatus': 'Filter by status',
  'scans.list.all': 'All',
  'scans.list.noneTitle': 'No scans yet',
  'scans.list.noneBody': 'Nothing has been scanned for this organisation. A scan checks one domain’s public DNS, email protection, HTTPS certificate and web security headers.',
  'scans.list.noMatch': 'No scans match your search or filter.',
  'scans.list.own': 'Your organisation',
  'scans.list.supplier': 'Supplier: {{name}}',
  'scans.list.ranAt': 'Scanned {{date}}',
  'scans.list.dueAt': 'Due {{date}}',
  'scans.list.failedAt': 'Failed {{date}}',
  'scans.list.startedAt': 'Started {{date}}',
  'scans.list.open': 'Open results',

  // --- Status --------------------------------------------------------------
  'scans.status.READY': 'Completed',
  'scans.status.PENDING': 'Waiting to run',
  'scans.status.IN_PROGRESS': 'Running',
  'scans.status.FAILED': 'Failed',

  // --- Glance --------------------------------------------------------------
  'scans.glance.critical': '{{count}} critical',
  'scans.glance.warning_one': '{{count}} warning',
  'scans.glance.warning_other': '{{count}} warnings',
  'scans.glance.clean': 'No critical or warning findings',
  'scans.glance.low': '{{count}} low-risk or informational',
  'scans.glance.expired': 'Details deleted',
  'scans.glance.waiting': 'Not run yet',
  'scans.glance.none': 'No findings recorded',
  'scans.glance.noDetail': 'Details unavailable',
  'scans.glance.running': 'In progress',
  'scans.glance.failed': 'No result: the scan did not complete',

  // --- Actions and cost ------------------------------------------------------
  'scans.run': 'Run now',
  'scans.runAgain': 'Run again',
  'scans.retry': 'Retry',
  'scans.running': 'Running…',
  'scans.noCharge': 'Already paid for: running it does not charge any more credits.',
  'scans.noChargeRetry': 'Retrying does not charge any more credits.',
  'scans.noChargeEarly': 'Already paid for. Running it now uses this run; it will not run again on {{date}}.',
  'scans.runSlow': 'A scan can take up to a minute. Keep this page open.',
  'scans.stuck': 'This scan has been marked as running since {{date}}. If it is not still running, run it again; this does not charge more credits.',
  'scans.scanAgain': 'Scan this domain again…',
  'scans.scanAgainCost': 'A new scan costs credits. You will see the price before anything is charged.',

  // --- Notices after actions -------------------------------------------------
  'scans.notice.scheduled': 'Scan paid for and scheduled. {{credits}} were charged; your balance is now {{balance}}.',
  'scans.notice.runFirst': 'Run the first scan now',
  'scans.notice.runDoneCounts': 'Scan of {{domain}} finished: {{summary}}.',
  'scans.notice.runFailed': 'The scan of {{domain}} did not complete, so there is no result. No additional credits were charged. You can retry it.',
  'scans.notice.reason': 'Reason reported: {{reason}}',
  'scans.notice.viewResults': 'View results',
  'scans.notice.cancelled_one': 'Schedule for {{domain}} cancelled. {{count}} unrun scan was removed. Completed scans and their results were kept.',
  'scans.notice.cancelled_other': 'Schedule for {{domain}} cancelled. {{count}} unrun scans were removed. Completed scans and their results were kept.',
  'scans.notice.dismiss': 'Dismiss',

  // --- Schedules -------------------------------------------------------------
  'scans.sched.title': 'Active schedules',
  'scans.sched.subtitle': 'Scans that are paid for and still to come.',
  'scans.sched.none': 'No scans are waiting to run.',
  'scans.sched.progress': '{{done}} of {{total}} runs completed',
  'scans.sched.remaining_one': '{{count}} run left',
  'scans.sched.remaining_other': '{{count}} runs left',
  'scans.sched.next': 'Next run: {{date}}',
  'scans.sched.cadence.SINGLE': 'One-off scan',
  'scans.sched.cadence.QUARTERLY': 'Quarterly',
  'scans.sched.cadence.MONTHLY': 'Monthly',
  'scans.sched.cadence.unknown': 'Scheduled scans',
  'scans.sched.runNext': 'Run next now',
  'scans.sched.cancel': 'Cancel',
  'scans.sched.cancelAria': 'Cancel the schedule for {{domain}}',
  'scans.cancel.title': 'Cancel this schedule?',
  'scans.cancel.body_one': 'The {{count}} scan that has not run yet for {{domain}} will be removed.',
  'scans.cancel.body_other': 'The {{count}} scans that have not run yet for {{domain}} will be removed.',
  'scans.cancel.noRefund': 'The credits already paid for them are not refunded.',
  'scans.cancel.forfeit': 'You will lose up to {{credits}} of prepaid scans.',
  'scans.cancel.kept': 'Scans that have already run, and their results, are kept.',
  'scans.cancel.keep': 'Keep schedule',
  'scans.cancel.confirm_one': 'Cancel and lose {{count}} scan',
  'scans.cancel.confirm_other': 'Cancel and lose {{count}} scans',
  'scans.cancel.working': 'Cancelling…',

  // --- Schedule / new scan dialog ---------------------------------------------
  'scans.new.title': 'Scan a domain',
  'scans.new.cancel': 'Cancel',
  'scans.new.target': 'What to scan',
  'scans.new.targetOwn': 'A domain of my organisation',
  'scans.new.domain': 'Domain',
  'scans.new.domainHint': 'The root domain only, for example example.com.',
  'scans.new.willScan': 'Will scan: {{domain}}',
  'scans.new.authorise': 'Only scan domains that belong to your organisation or to your suppliers.',
  'scans.new.vendorsFailed': 'Could not load your suppliers. You can still scan your own domain.',
  'scans.new.frequency': 'How often',
  'scans.new.freq.SINGLE': 'Once',
  'scans.new.freq.QUARTERLY': 'Every 3 months',
  'scans.new.freq.MONTHLY': 'Every month',
  'scans.new.runs_one': '{{count}} scan',
  'scans.new.runs_other': '{{count}} scans',
  'scans.new.optionCost': '{{credits}}, charged now',
  'scans.new.summaryTitle': 'What you will be charged',
  'scans.new.chargedNow': 'Charged now',
  'scans.new.balanceNow': 'Your balance',
  'scans.new.balanceAfter': 'Balance after',
  'scans.new.shortfall': 'You do not have enough credits. You need {{shortfall}} more.',
  'scans.new.noRefund': 'Credits for scans you later cancel are not refunded.',
  'scans.new.pricingFailed': 'Could not load the price. Nothing can be charged until the price is shown.',
  'scans.new.pricingLoading': 'Loading the price…',
  'scans.new.priceUnavailable': 'Price unavailable',
  'scans.new.ack_one': 'I understand that {{credits}} will be charged now for {{count}} scan of {{domain}}.',
  'scans.new.ack_other': 'I understand that {{credits}} will be charged now for {{count}} scans of {{domain}}, and that credits for scans I cancel are not refunded.',
  'scans.new.confirmSingle': 'Pay {{credits}} and scan',
  'scans.new.confirmSchedule': 'Pay {{credits}} and schedule',
  'scans.new.working': 'Charging…',
  'scans.new.domainInvalid': 'That does not look like a valid domain.',
  'scans.new.domainSubdomain': 'Use the root domain instead: {{root}}.',

  // --- Detail ----------------------------------------------------------------
  'scans.detail.back': 'All scans',
  'scans.detail.loadFailed': 'Could not load this scan. No findings are shown because they could not be retrieved, which is not the same as there being none.',
  'scans.detail.notFound': 'This scan no longer exists.',
  'scans.detail.availableUntil': 'Findings are kept until {{date}} and are then deleted. The counts stay.',
  'scans.detail.summary': 'Summary',
  'scans.detail.tile.critical': 'Critical',
  'scans.detail.tile.warning': 'Warnings',
  'scans.detail.tile.low': 'Low-risk or OK',
  'scans.detail.tile.lowHint': 'includes informational items',
  'scans.detail.pending': 'This scan has not run yet. It was due {{date}}.',
  'scans.detail.running': 'This scan is running. This page updates on its own.',
  'scans.detail.failed': 'This scan did not complete, so there are no findings to show. That does not mean the domain is fine: nothing was checked.',
  'scans.detail.expired': 'The detailed findings of this scan were deleted after the retention period. Only the counts are left, so what was found can no longer be shown.',
  'scans.detail.unreadable': 'The findings of this scan could not be read, although the scan recorded {{count}} findings. Scan the domain again to get a readable result; a new scan is charged.',
  'scans.detail.noneRecorded': 'No findings were recorded for this scan. That is not the same as a clean result: a completed scan always records at least one finding. Scan the domain again to get a result; a new scan is charged.',
  'scans.detail.findings': 'Findings',
  'scans.detail.expandAll': 'Expand all',
  'scans.detail.collapseAll': 'Collapse all',
  'scans.detail.areaIssues_one': '{{count}} needs attention',
  'scans.detail.areaIssues_other': '{{count}} need attention',
  'scans.detail.areaClear': 'No issues',
  'scans.detail.vendorOwner': 'This is {{vendor}}’s domain, so you cannot change it yourself. Raise this finding with them.',

  // --- Finding -----------------------------------------------------------------
  'scans.band.critical': 'Critical',
  'scans.band.warning': 'Warning',
  'scans.band.ok': 'OK',
  'scans.band.info': 'Info',
  'scans.finding.checked': 'What was checked',
  'scans.finding.found': 'What was found',
  'scans.finding.why': 'Why it matters',
  'scans.finding.fix': 'What to do',
  'scans.finding.noFix': 'Nothing to do.',
  'scans.finding.headerMissing': 'The website’s response did not include this header.',
  'scans.finding.whoToAsk': 'Who can fix this',

  // --- Areas (the scanner's categories) ---------------------------------------
  'scans.area.DNS': 'Domain name set-up (DNS)',
  'scans.area.Email': 'Email protection (SPF, DMARC, mail servers)',
  'scans.area.TLS': 'HTTPS certificate (TLS)',
  'scans.area.HTTP headers': 'Web security headers',
  'scans.area.other': 'Other checks',
  'scans.owner.DNS': 'Whoever manages this domain’s DNS: usually your IT team, domain registrar or hosting provider.',
  'scans.owner.Email': 'Whoever manages this domain’s DNS and email: usually your IT team, email provider or domain registrar.',
  'scans.owner.TLS': 'Whoever runs the website or web server: usually your web developer, hosting provider or IT team.',
  'scans.owner.HTTP headers': 'Whoever runs the website or web server: usually your web developer, hosting provider or IT team.',
  'scans.owner.other': 'Whoever runs this domain’s website and DNS.',

  // --- Explanations: what a check looks at, and why it matters ----------------
  'scans.topic.dnsResolve.checked': 'Whether the domain name points to a server (an IPv4 address record).',
  'scans.topic.dnsResolve.why': 'If the domain does not resolve, nobody can reach the website or services on it, and the rest of the scan has little to look at. It is often a typo or DNS that was never set up. A single failed lookup can also be a temporary DNS problem, so run the scan again before acting on it.',
  'scans.topic.dnsIpv6.checked': 'Whether the domain also publishes an IPv6 address.',
  'scans.topic.dnsIpv6.why': 'IPv6 is a newer way of addressing servers. Not having it is not a security problem. It is shown for information only.',
  'scans.topic.dnsNs.checked': 'How many nameservers the domain publishes. Nameservers are the servers that answer questions about the domain.',
  'scans.topic.dnsNs.why': 'With only one nameserver, an outage there makes the whole domain, including its website and email, unreachable. Two or more on separate networks keep it working.',
  'scans.topic.spf.checked': 'Whether the domain publishes an SPF record, a public list of the servers allowed to send email for it, and how strict that list is.',
  'scans.topic.spf.why': 'Without a strict SPF record it is easier for attackers to send fake email that appears to come from this domain, for example invoices or password resets aimed at customers and staff.',
  'scans.topic.dmarc.checked': 'Whether the domain publishes a DMARC policy, which tells receiving mail systems what to do with email that fails the sender checks.',
  'scans.topic.dmarc.why': 'DMARC is what makes receivers block forged email from the domain. “Monitor only” collects reports but blocks nothing. “Quarantine” sends suspicious mail to spam. “Reject” refuses it outright and is the strongest setting. Move up one step at a time so genuine email is not blocked by mistake.',
  'scans.topic.mx.checked': 'Whether the domain publishes mail servers (MX records).',
  'scans.topic.mx.why': 'No mail servers just means the domain does not receive email, which is normal for a domain used only for a website. It is shown for information only.',
  'scans.topic.tlsReach.checked': 'Whether a secure HTTPS connection can be made to the domain on port 443.',
  'scans.topic.tlsReach.why': 'Without HTTPS, what visitors send and receive can be read or altered in transit, and browsers show warnings. It can also mean the connection timed out or was blocked, or that the site is only served at a “www.” address, which this scan does not test.',
  'scans.topic.tlsProtocol.checked': 'Which version of the TLS encryption protocol the server chose for this connection.',
  'scans.topic.tlsProtocol.why': 'TLS 1.0 and 1.1 are obsolete and have known weaknesses. TLS 1.2 and 1.3 are current. The scan records only the version the server chose; it does not test whether the server would also accept older versions.',
  'scans.topic.tlsCert.checked': 'The expiry date of the HTTPS certificate.',
  'scans.topic.tlsCert.why': 'An expired certificate makes browsers show a full-page warning and breaks systems that connect securely. A certificate close to expiry is a warning because a missed renewal causes an outage. Automatic renewal prevents it.',
  'scans.topic.tlsTrust.checked': 'Whether the certificate was issued by an authority browsers trust and matches the domain name.',
  'scans.topic.tlsTrust.why': 'A certificate that is not trusted, or does not match the domain, makes browsers warn visitors and makes it easier for an attacker to pose as the site.',
  'scans.topic.httpReach.checked': 'Whether the website answered a normal HTTPS request.',
  'scans.topic.httpReach.why': 'If there is no answer, the header checks could not run, so the security headers are unknown, not good or bad. The site may be down, may block the scanner, or may only be served at a “www.” address.',
  'scans.topic.hsts.checked': 'Whether the website sends an HSTS header, which tells browsers to always use HTTPS.',
  'scans.topic.hsts.why': 'Without HSTS, a visitor on an untrusted network, such as café wifi, can be silently pushed onto an insecure connection where attackers can read or change what they see.',
  'scans.topic.csp.checked': 'Whether the website sends a Content-Security-Policy header, which limits where a page may load scripts from.',
  'scans.topic.csp.why': 'It limits the damage if an attacker manages to inject malicious code into a page. Missing it is common and is not a flaw by itself, but it removes a safety net.',
  'scans.topic.xcto.checked': 'Whether the website sends the X-Content-Type-Options: nosniff header.',
  'scans.topic.xcto.why': 'It stops browsers guessing a file’s type, which can be abused to make an uploaded file run as a script.',
  'scans.topic.xfo.checked': 'Whether the response includes the X-Frame-Options header. A Content-Security-Policy frame-ancestors rule gives the same protection, but this scan does not look for it.',
  'scans.topic.xfo.why': 'Without it, another website can display this site inside a hidden frame and trick visitors into clicking things (“clickjacking”). If the site already uses frame-ancestors, this finding can be ignored.',
  'scans.topic.referrer.checked': 'Whether the website sends a Referrer-Policy header.',
  'scans.topic.referrer.why': 'Without it, the address of the page a visitor came from can be passed to other websites they click through to. That address can contain private details. Low risk.',
  'scans.topic.serverBanner.checked': 'Whether the Server header reveals a software name with a version number.',
  'scans.topic.serverBanner.why': 'Exact versions tell an attacker which known flaws to try. Hiding the version does not fix any flaw, but it removes a free hint.',
  'scans.topic.unknown.checked': 'A check in the “{{area}}” group.',
  'scans.topic.unknown.why': 'No plain-language explanation is available for this check yet. Use what was found and what to do, below.',

  // --- Errors specific to this screen -------------------------------------------
  'scans.err.alreadyCancelled': 'This schedule was already cancelled.',
} as const;

export type ScanStringKey = keyof typeof SCAN_STRINGS;
/** A key as written in code: plural pairs are addressed by their base key. */
export type ScanKey = ScanStringKey extends infer K
  ? K extends `${infer B}_one` | `${infer B}_other` ? B : K
  : never;

/**
 * Translate a scan string. The English default is supplied inline, including
 * the right plural form, so a missing locale entry never renders a raw key.
 */
export function useScanT() {
  const { t, i18n } = useTranslation();
  return useCallback(
    (key: ScanKey, options: Record<string, unknown> = {}): string => {
      const table = SCAN_STRINGS as Record<string, string>;
      let fallback = table[key];
      if (fallback === undefined && typeof options.count === 'number') {
        const form = new Intl.PluralRules(i18n.language).select(options.count);
        fallback = table[`${key}_${form}`] ?? table[`${key}_other`];
      }
      return t(key, { defaultValue: fallback ?? key, ...options });
    },
    [t, i18n.language],
  );
}

/** "15 credits" / "1 credit", in the user's language. */
export function useCredits() {
  const tt = useScanT();
  return useCallback((n: number) => tt('scans.credits', { count: n }), [tt]);
}

/** Errors for scan actions: this screen's own codes first, then the shared ones. */
export function useScanError() {
  const describe = useApiError();
  const tt = useScanT();
  return useCallback(
    (error: unknown): string => {
      if (error instanceof ApiError) {
        if (error.code === 'SCAN_CONFIG_INACTIVE') return tt('scans.err.alreadyCancelled');
        if (error.code === 'VALIDATION_FAILED') {
          const issues = (error.details as { issues?: Array<{ path: string; message: string }> } | undefined)?.issues ?? [];
          const domainIssue = issues.find((i) => i.path === 'domain');
          if (domainIssue?.message.startsWith('SUBDOMAIN_NOT_ALLOWED:')) {
            return tt('scans.new.domainSubdomain', { root: domainIssue.message.split(':')[1] });
          }
          if (domainIssue) return tt('scans.new.domainInvalid');
        }
      }
      return describe(error);
    },
    [describe, tt],
  );
}
